import React, { useState } from 'react';
import styled from 'styled-components';
import { Search } from 'lucide-react';
import Layout from './layout';
import { campaignData } from '../data/mockData';
import CampaignManagementModal from '../Modal/CampaignManagementModal';

// Styled Components
const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: 3rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #333333;
  }
`;

const AddButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #333333;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: none;
  background-color: ${props => props.active ? '#1976D2' : 'transparent'};
  color: ${props => props.active ? 'white' : '#1976D2'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  min-width: 40px;
  text-decoration: ${props => props.active ? 'none' : 'underline'};

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? '#1565C0' : '#E3F2FD'};
  }

  &:disabled {
    color: #9E9E9E;
    text-decoration: none;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: ${props => props.align || 'center'};
  font-size: 16px;
  font-weight: 400;
  color: #A3A3A3;
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 14px;
  color: #6F6F6F;
  text-align: ${props => props.align || 'center'};
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
`;

const ConsultantContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const ConsultantTag = styled.span`
  background-color: #1D87F0;
  color: white;
  padding: 0.25rem 0.75rem;
  font-size: 14px;
  display: inline-block;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: #404E67;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Campaign = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 5;

  const totalPages = Math.ceil(campaignData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = campaignData.slice(startIndex, endIndex);

  return (
    <Layout>
      <PageContainer>
        {/* Header Section */}
        <HeaderSection>
          {/* Search Box */}
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="ค้นหาแคมเปญ"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <SearchButton>
              <Search size={18} />
            </SearchButton>
          </SearchContainer>

          {/* Add Button */}
          <AddButton onClick={() => setIsModalOpen(true)}>
            เพิ่มแคมเปญ
          </AddButton>
        </HeaderSection>

        {/* Pagination */}
        <PaginationContainer>
          <PaginationButton
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            previous
          </PaginationButton>
          {[...Array(totalPages)].map((_, index) => (
            <PaginationButton
              key={index + 1}
              active={currentPage === index + 1}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </PaginationButton>
          ))}
          <PaginationButton
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            next
          </PaginationButton>
        </PaginationContainer>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>ลำดับ</TableHeader>
                <TableHeader>รหัสแคมเปญ</TableHeader>
                <TableHeader>บริษัท</TableHeader>
                <TableHeader>ชื่อแคมเปญ</TableHeader>
                <TableHeader>ที่ปรึกษา</TableHeader>
                <TableHeader>ระยะเวลาแคมเปญ <br></br>(รายบุคคล)</TableHeader>
                <TableHeader>ค่าใช้จ่าย</TableHeader>
                <TableHeader>รหัสโปรโมชั่น</TableHeader>
                <TableHeader>วันที่เริ่มต้น</TableHeader>
                <TableHeader>วันที่สิ้นสุด</TableHeader>
                <TableHeader></TableHeader>
              </tr>
            </TableHead>
            <tbody>
              {currentData.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.id}</TableCell>
                  <TableCell>{campaign.code}</TableCell>
                  <TableCell>{campaign.company}</TableCell>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>
                    <ConsultantContainer>
                      {campaign.consultants.map((consultant, index) => (
                        <ConsultantTag key={index}>{consultant}</ConsultantTag>
                      ))}
                    </ConsultantContainer>
                  </TableCell>
                  <TableCell>{campaign.duration}</TableCell>
                  <TableCell>{campaign.cost}</TableCell>
                  <TableCell>{campaign.promoCode}</TableCell>
                  <TableCell>{campaign.startDate}</TableCell>
                  <TableCell>{campaign.endDate}</TableCell>
                  <TableCell>
                    <ActionButtonsContainer>
                      <TextButton>ปรับปรุงสมาชิก</TextButton>
                      <TextButton>แก้ไข</TextButton>
                    </ActionButtonsContainer>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </PageContainer>

      {/* Campaign Management Modal */}
      <CampaignManagementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </Layout>
  );
};

export default Campaign;
